import { ChangeDetectorRef, Component, ElementRef, EventEmitter, HostListener, Input, OnInit, Output, ViewChild, forwardRef, inject } from '@angular/core';
import { NgClass } from '@angular/common';
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms';

import { take } from 'rxjs';
import { TranslateModule } from '@ngx-translate/core';

import { ImageComponent } from '@atoms/image/image.component';
import { InputErrorComponent } from '@atoms/input-error/input-error.component';
import { PhoneInputService } from '@services/phone-input.service';
import { SsrService } from '@services/ssr.service';
import { PhoneInput, PhoneInputItem } from '@models/phone-input.model';

@Component({
  selector: 'app-phone-input',
  templateUrl: './phone-input.component.html',
  imports: [NgClass, FormsModule, TranslateModule, ImageComponent, InputErrorComponent],
  styleUrls: ['./phone-input.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: forwardRef(() => PhoneInputComponent),
    },
  ],
})
export class PhoneInputComponent implements ControlValueAccessor, OnInit {
  private cd = inject(ChangeDetectorRef);
  private phoneInputService = inject(PhoneInputService);
  public ssrService = inject(SsrService);
  @Input() selected?: string;
  @Input() preferred: string[] = [];
  @Input('country-code') countryCode?: string[];
  @Input('country-key') countryKey: 'code' | 'name' = 'code';
  @Input() type: 'phone' | 'country' = 'phone';
  @Input() search: boolean = true;
  @Input() required: boolean = false;
  @Input() disabled: boolean = false;
  @Input() error: boolean = false;
  @Input('error-message') errorMessage?: string;
  @Input() value?: PhoneInputItem;
  @Output() valueChange: EventEmitter<PhoneInputItem> = new EventEmitter<PhoneInputItem>();
  @ViewChild('searchInput') searchInput!: ElementRef<HTMLCanvasElement>;
  public countries: PhoneInputItem[] = [];
  public preferredCountries: PhoneInputItem[] = [];
  public active: boolean = false;
  public searchValue: string = '';
  public searchTimeout: any;
  private lastClickInside: boolean = false;
  public onChange = (value: string | undefined) => {};
  public onTouched = (value: boolean) => {};

  @HostListener('click') clickInside(): void {
    this.lastClickInside = true;
  }

  @HostListener('document:click') clickout(): void {
    if (!this.lastClickInside) {
      this.active = false;
      if (this.searchTimeout) clearTimeout(this.searchTimeout);
      this.searchValueFromValue();
    }
    this.lastClickInside = false;
  }

  toggle(): void {
    this.active = !this.active;
    if (this.searchTimeout) clearTimeout(this.searchTimeout);
    this.searchValueFromValue();
  }

  select(country?: PhoneInputItem): void {
    this.value = country;
    this.valueChange.emit(this.value);
    this.active = false;
    if (this.searchTimeout) clearTimeout(this.searchTimeout);
    this.searchValueFromValue();
    const value: string | undefined = country === undefined ? undefined : this.type === 'phone' ? country.dialCode : country[this.countryKey];
    this.onChange(value);
    this.onTouched(true);
  }

  searchValueFromValue(): void {
    if (!this.search) return;
    if (this.active) {
      this.searchValue = '';
      this.searchInput?.nativeElement.focus();
    } else if (this.value) {
      this.searchValue = this.type === 'phone' ? this.value.dialCode : this.value.name;
      this.searchInput?.nativeElement.blur();
    }
    this.searchValueChange();
  }

  searchValueChange(): void {
    this.countries.forEach((country: PhoneInputItem) => {
      if (this.searchValue === '') country.visible = true;
      else country.visible = country.name.toLowerCase().includes(this.searchValue.toLowerCase()) || country.code.toLowerCase().includes(this.searchValue.toLowerCase()) || country.dialCode.includes(this.searchValue);
    });
  }

  searchValueFocusout(): void {
    this.searchTimeout = setTimeout(() => {
      this.active = false;
      this.searchValueFromValue();
    }, 1000);
    const visibleCountries: PhoneInputItem[] = this.countries.filter((country: PhoneInputItem) => country.visible);
    if (visibleCountries.length === 1) this.select(visibleCountries[0]);
  }

  getData(): void {
    this.phoneInputService
      .getPhoneCodes()
      .pipe(take(1))
      .subscribe((response: PhoneInput) => {
        response.preferredCountries = [];
        const countries: PhoneInputItem[] = this.countryCode ? [] : response.countries;
        for (let item of response.countries) {
          if (this.countryCode && this.countryCode.includes(item.code)) countries.push(item);
          if (this.preferred?.includes(item.code)) {
            item.preferred = true;
            response.preferredCountries.push(item);
          }
          if (this.type === 'phone' && this.selected === item.dialCode) response.selected = item;
          else if (this.type === 'country' && this.selected === item[this.countryKey]) response.selected = item;
        }

        this.countries = countries;
        this.preferredCountries = response.preferredCountries;
        if (this.type === 'country') this.value = response.selected ?? undefined;
        else this.value = response.selected ? response.selected : response.preferredCountries.length ? response.preferredCountries[0] : response.countries[0];
        this.valueChange.emit(this.value);
        this.searchValueFromValue();
        this.cd.detectChanges();
      });
  }

  writeValue(obj: any): void {
    this.selected = obj;
    this.getData();
  }

  registerOnChange(fn: (value: string | undefined) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: (value: boolean) => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  ngOnInit(): void {
    this.getData();
  }
}
